import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React from 'react';
import { useAccessControl } from '../../hooks/useAccessControl';
import TrialStartButton from './TrialStartButton';
import TrialBanner from './TrialBanner';
import TrialExpiredModal from './TrialExpiredModal';
import AppLoading from '../AppLoading';
import './Trial.css';

/**
 * Feature Gate Component
 * 
 * Wraps features that require active subscription OR trial.
 * Automatically shows:
 * - Trial start button (if no access)
 * - Trial banner (if on trial)
 * - Expired modal (if trial expired)
 * - Content (if has access)
 * 
 * Usage:
 *   <FeatureGate>
 *     <YourProtectedComponent />
 *   </FeatureGate>
 */
const FeatureGate = ({ 
  children, 
  showBanner = true,
  loadingComponent = null
}) => {
  useUiLanguage();
  const { hasAccess, accessType, loading, trial, error } = useAccessControl();

  // Loading state
  if (loading) {
    return loadingComponent || <AppLoading inline />;
  }

  // Error state
  if (error) {
    return (
      <div className="feature-gate-error">
        <p>
          <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--app-tone-ff6b6b, #ff6b6b)', marginRight: '8px' }}></i>{uiText("Error checking access: ")}{uiText(error)}
        </p>
        <button onClick={() => window.location.reload()}>
          <i className="fa-solid fa-rotate-right" style={{ marginRight: '6px' }}></i>{uiText("Retry")}</button>
      </div>
    );
  }

  // No access - show options
  if (!hasAccess) {
    // Trial expired
    if (accessType === 'trial_expired') {
      return <TrialExpiredModal trial={trial} />;
    }

    // No trial or subscription
    return (
      <div className="feature-gate-no-access">
        <div className="feature-gate-card">
          <h2>{uiText("Start Your Free Trial")}</h2>
          <p>{uiText("Get 14 days of full access to ATS + 10 TSE test credits. No credit card required!")}</p>
          <TrialStartButton />
        </div>
      </div>
    );
  }

  // Has access - show content
  return (
    <>
      {showBanner && accessType === 'trial' && (
        <TrialBanner trial={trial} />
      )}
      {children}
    </>
  );
};

export default FeatureGate;
